import {
    createReport,
    deleteReport,
    duplicateReport,
    executeWidget,
    fetchReport,
    fetchReports,
} from '../api/reports.api';
import useOptimisticMutation from './useOptimisticMutation';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { v4 as uuid } from 'uuid';

export const REPORTS_KEY = 'reports';
export const WIDGET_KEY = 'widget';

const nowIso = () => new Date().toISOString();

// Reports own their freshness via the saver (flush-on-blur + gated
// refetch-on-focus); disable react-query's blind focus refetch so it can't
// clobber unsaved local edits. Callers may still override.
export const useReports = (options = {}) =>
    useQuery(REPORTS_KEY, fetchReports, {
        refetchOnWindowFocus: false,
        ...options,
    });

export const useReport = (id, options = {}) =>
    useQuery([REPORTS_KEY, id], () => fetchReport(id), {
        enabled: Boolean(id),
        refetchOnWindowFocus: false,
        ...options,
    });

export const widgetQuery = (args) => ({
    queryKey: [WIDGET_KEY, args],
    queryFn: () => executeWidget(args),
    refetchOnMount: 'always',
    staleTime: 0,
    retry: 1,
});

// Stands in only until the refetch lands, which `widgetQuery`'s
// `staleTime: 0` + `refetchOnMount: 'always'` guarantee. Never overwrites an
// existing entry: a key visited before holds the server's own answer.
export const seedWidgetResult = (qc, args, output) => {
    if (!output) return;
    if (qc.getQueryData([WIDGET_KEY, args]) !== undefined) return;
    qc.setQueryData([WIDGET_KEY, args], output);
};

// Optimistically append a temp row; on success swap it for the real server row
// and seed the single-report cache so a report view opened right after create
// starts version-primed for the saver. No refetch needed (the optimistic write
// plus the server row is the final state).
export const useCreateReport = () => {
    const qc = useQueryClient();
    return useMutation(createReport, {
        onMutate: (config) => {
            qc.cancelQueries(REPORTS_KEY);
            const snapshot = qc.getQueryData(REPORTS_KEY);
            const tempId = `optimistic-${uuid()}`;
            const timestamp = nowIso();
            qc.setQueryData(REPORTS_KEY, (prev = []) => [
                ...prev,
                {
                    id: tempId,
                    created: timestamp,
                    updated: timestamp,
                    version: 0,
                    config: config ?? {},
                },
            ]);
            return { snapshot, tempId };
        },
        onError: (_err, _config, ctx) => {
            if (ctx?.snapshot !== undefined) {
                qc.setQueryData(REPORTS_KEY, ctx.snapshot);
            }
        },
        onSuccess: (created, _config, ctx) => {
            qc.setQueryData(REPORTS_KEY, (prev = []) =>
                prev.map((report) =>
                    report.id === ctx?.tempId ? created : report,
                ),
            );
            qc.setQueryData([REPORTS_KEY, created.id], created);
        },
    });
};

// Server-side duplicate — copies the document wholesale with no client
// round-trip. Seeds both caches so the copy shows immediately; callers pass a
// per-call onSuccess to navigate when needed.
export const useDuplicateReport = () => {
    const qc = useQueryClient();
    return useMutation(duplicateReport, {
        onSuccess: (created) => {
            qc.setQueryData(REPORTS_KEY, (prev = []) => [...prev, created]);
            qc.setQueryData([REPORTS_KEY, created.id], created);
        },
    });
};

export const useDeleteReport = () =>
    useOptimisticMutation(REPORTS_KEY, deleteReport, (prev = [], id) =>
        prev.filter((report) => report.id !== id),
    );
